import type { TranslatedString } from '../api/types';

export const ALL_LOCALES = ['en', 'de', 'hu'] as const;
export type Locale = (typeof ALL_LOCALES)[number];
export const FALLBACK_LOCALE: Locale = 'en';

export const isLocale = (v: string | undefined | null): v is Locale =>
  v != null && (ALL_LOCALES as readonly string[]).includes(v);

// Pick the best string from a TranslatedString. Prefers `preferred` if set,
// else the first non-empty locale in canonical ALL_LOCALES order.
// Returns undefined if no locale has a usable value.
//
// Also tolerates an already-localized plain string. Some backend fields are
// role-dependent: a product `name`, for instance, arrives as a nested
// TranslatedString on admin payloads (serialized via TranslatableAttributes)
// but as a plain, already-localized string on customer-facing ones. Accepting
// both lets a single call site localize the value regardless of which shape
// the backend sent.
export function pickLocalized(
  t: TranslatedString | string | undefined | null,
  preferred?: Locale,
): string | undefined {
  if (t == null) return undefined;
  if (typeof t === 'string') return t || undefined;
  // Only ever hand back strings. A locale slot can hold a nested translation
  // hash (seen in production after a backend Mobility whole-hash write);
  // returning it would crash whatever renders the result as a React child.
  if (preferred && typeof t[preferred] === 'string' && t[preferred]) {
    return t[preferred];
  }
  for (const l of ALL_LOCALES) {
    const v = t[l];
    if (typeof v === 'string' && v) return v;
  }
  return undefined;
}
